/**
 * Gössl Theme - Cart Drawer
 * Handles slide-out cart functionality with AJAX updates
 */

(function() {
  'use strict';

  const SELECTORS = {
    drawer: '[data-cart-drawer]',
    closeBtn: '[data-cart-drawer-close]',
    body: '[data-cart-drawer-body]',
    items: '[data-cart-items]',
    empty: '[data-cart-empty]',
    footer: '[data-cart-footer]',
    subtotal: '[data-cart-subtotal]',
    quantityInput: '[data-quantity-input]',
    quantityDecrease: '[data-quantity-decrease]',
    quantityIncrease: '[data-quantity-increase]',
    removeItem: '[data-remove-item]',
    cartToggle: '[data-cart-toggle]',
    // a11y #902: where focus goes on close when the element that opened the drawer is gone
    // (the 2026 header's cart icon carries data-g26-cart-open, not data-cart-toggle)
    cartOpener: '[data-g26-cart-open], [data-cart-toggle]'
  };

  // r162: cap every cart request - a request that never settles on a flaky
  // mobile connection left isUpdating=true and swallowed every further tap.
  // Older WebKit without AbortSignal.timeout simply runs without a cap.
  const timeoutSignal = (ms) => (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) ? AbortSignal.timeout(ms) : undefined;

  class CartDrawer {
    constructor() {
      this.drawer = document.querySelector(SELECTORS.drawer);
      if (!this.drawer) return;

      this.isOpen = false;
      this.isUpdating = false;
      this.overlay = null;
      this.pendingFocus = null;

      this.init();
    }

    init() {
      this.bindEvents();
      this.bindCartToggle();
    }

    bindEvents() {
      // Quantity + close buttons — all delegated on the drawer root so they
      // survive the section-rendering innerHTML swaps
      this.drawer.addEventListener('click', (e) => {
        if (e.target.closest(SELECTORS.closeBtn)) { this.close(); return; }
        const checkout = e.target.closest('[data-cart-checkout]');
        if (checkout && window.g26preorder && window.g26preorder.capCart) {
          e.preventDefault();
          this.goToCheckout(checkout.href);
          return;
        }
        const decrease = e.target.closest(SELECTORS.quantityDecrease);
        const increase = e.target.closest(SELECTORS.quantityIncrease);
        const remove = e.target.closest(SELECTORS.removeItem);

        if (decrease) {
          const key = decrease.dataset.quantityDecrease;
          this.updateQuantity(key, -1);
        }

        if (increase) {
          const key = increase.dataset.quantityIncrease;
          this.updateQuantity(key, 1);
        }

        if (remove) {
          const key = remove.dataset.removeItem;
          this.removeItem(key);
        }
      });

      // Quantity input change — validate + clamp to min/max
      // (r190: the Lieblingsgeschäft picker left the drawer; the cart page keeps it)
      this.drawer.addEventListener('change', (e) => {
        if (e.target.matches(SELECTORS.quantityInput)) {
          const key = e.target.dataset.quantityInput;
          let quantity = parseInt(e.target.value, 10);
          // Clamp to valid range, default invalid → 1
          if (isNaN(quantity) || quantity < 0) quantity = 1;
          if (quantity > 99) quantity = 99;
          // Reflect clamped value in UI immediately
          e.target.value = quantity;
          this.setQuantity(key, quantity);
        }
      });

      // Listen for cart updates from other components
      document.addEventListener('cart:updated', () => {
        this.refresh();
      });

      // Listen for add-to-cart: refresh contents and open drawer (detail.opener: who gets focus back on close)
      document.addEventListener('cart:open', (e) => {
        const opener = e.detail && e.detail.opener;
        this.refresh().then(() => this.open(opener));
      });

      // r188: pieces above a size's stock were moved onto a pre-order line (assets/preorder-cart.js) -
      // say so at the top of the drawer once it shows the new lines
      document.addEventListener('cart:preorder-split', (e) => {
        this.pendingNotice = e.detail && e.detail.message;
      });
      // r190: any other calm note for the next drawer render (e.g. an add-on the PDP could not add)
      document.addEventListener('cart:notice', (e) => {
        if (e.detail && e.detail.message) this.pendingNotice = e.detail.message;
      });
      // r190: a line got another colour or size (assets/cart-line-options.js) - redraw from the server
      document.addEventListener('cart:line-swapped', () => {
        this.refresh();
      });

      // r162: a page restored from Safari's back-forward cache (iPhone back
      // gesture) carries a stale drawer and, if a request was in flight when
      // the customer left, a stuck update guard - the X then did nothing until
      // the customer left the site and came back. Re-sync on restore.
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) { this.isUpdating = false; this.refresh(); }
      });
    }

    bindCartToggle() {
      document.querySelectorAll(SELECTORS.cartToggle).forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle();
        });
      });
    }

    open(opener) {
      if (this.isOpen) return;

      this.isOpen = true;
      this.drawer.hidden = false;

      // Force reflow
      this.drawer.offsetHeight;

      this.drawer.classList.add('is-open');
      document.body.classList.add('cart-drawer-open');

      // a11y #902: the shared overlay behaviour - focus moves to the close button, Tab stays in the drawer,
      // Escape closes, and focus goes back to the opener (or to the header cart icon when that is gone)
      if (!this.overlay && window.Goessl?.overlayFocus) {
        this.overlay = window.Goessl.overlayFocus(this.drawer, {
          onEscape: () => this.close(),
          fallback: () => document.querySelector(SELECTORS.cartOpener)
        });
      }
      this.overlay?.open(opener);
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('cart-drawer-open');

      // Release the trap, focus back to the opener
      this.overlay?.close();

      // Hide after transition
      setTimeout(() => {
        if (!this.isOpen) {
          this.drawer.hidden = true;
        }
      }, 300);
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    async updateQuantity(key, delta) {
      if (this.isUpdating) return;

      const input = this.drawer.querySelector(`[data-quantity-input="${key}"]`);
      if (!input) return;

      const currentQuantity = parseInt(input.value, 10);
      const newQuantity = Math.max(0, currentQuantity + delta);

      await this.setQuantity(key, newQuantity);
    }

    // r188: the checkout door. A quantity typed a moment before can still be on its way (the input's change
    // fires as the button is pressed), so wait for it, then cap once more: whatever filled the cart, no line
    // without the plan reaches the checkout with more pieces than its size has in stock. When pieces moved,
    // the customer stays and sees the new pre-order line before paying; a cap that cannot run lets them pass.
    async goToCheckout(href) {
      if (this.checkingOut) return;
      this.checkingOut = true;
      const moves = window.g26preorder.moves || 0;
      try {
        for (let i = 0; this.isUpdating && i < 160; i++) await new Promise((r) => setTimeout(r, 100));
        const capped = await window.g26preorder.capCart();
        if (capped.moved) { await this.refresh(); return; }
        if ((window.g26preorder.moves || 0) !== moves) return; // the racing update moved and redrew already
      } finally {
        this.checkingOut = false;
      }
      window.location.href = href;
    }

    async setQuantity(key, quantity) {
      if (this.isUpdating) return;
      this.isUpdating = true;

      // a11y #902: disabling the line drops focus from the button that was pressed - remember it
      this.pendingFocus = this.focusedKey();
      this.setLoading(true);
      this.setItemUpdating(key, true);
      this.clearError();

      try {
        const response = await fetch(window.g26url('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity, sections: 'cart-drawer' }),
          signal: timeoutSignal(15000)
        });

        if (!response.ok) {
          // Try to extract Shopify error message
          let serverMsg = null;
          try {
            const errorBody = await response.json();
            serverMsg = errorBody.description || errorBody.message;
          } catch (_) {}
          const err = new Error(serverMsg || `HTTP ${response.status}`);
          err.type = response.status >= 500 ? 'server' : 'validation';
          throw err;
        }

        const cart = await response.json();
        if (cart.sections && cart.sections['cart-drawer']) {
          this.renderSection(cart.sections['cart-drawer']);
        }
        this.updateCartCount(cart.item_count);

        // r188: + on a line sold from stock never goes past that stock - the extra pieces move onto a
        // pre-order line; redraw from the server so both lines show
        if (quantity > 0 && window.g26preorder && window.g26preorder.capCart) {
          const capped = await window.g26preorder.capCart();
          if (capped.moved) await this.refresh();
        }

      } catch (error) {
        console.error('Cart update error:', error);

        // Distinguish network vs server vs validation errors
        const t = window.GoesslCartTranslations || {};
        let message;
        if (error.name === 'TypeError' || error.name === 'TimeoutError' || error.name === 'AbortError' || error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
          message = t.error_network || 'Keine Verbindung. Bitte prüfen Sie Ihre Internetverbindung.';
        } else if (error.type === 'server') {
          message = t.error_server || 'Der Warenkorb konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.';
        } else {
          message = error.message && !error.message.startsWith('HTTP')
            ? error.message
            : (t.error_generic || 'Der Artikel konnte nicht aktualisiert werden.');
        }

        // Reset the line to its real quantity first, then say why: the redraw replaces the drawer's markup, and an
        // error shown before it was gone again a tenth of a second later (a11y #902). Shown after the awaited
        // redraw, it also appears when that redraw fails too (no connection), which a note left for the next
        // render (pendingNotice) would not
        const input = this.drawer.querySelector(`[data-quantity-input="${key}"]`);
        if (input) await this.refresh();
        this.showError(message, () => this.setQuantity(key, quantity));
      } finally {
        this.isUpdating = false;
        this.setLoading(false);
        this.setItemUpdating(key, false);
        // no redraw took it (the request failed): focus the re-enabled control again
        this.restoreFocus(this.pendingFocus);
        this.pendingFocus = null;
      }
    }

    setItemUpdating(key, updating) {
      const item = this.drawer.querySelector(`[data-cart-item="${CSS.escape(key)}"]`);
      if (item) {
        item.classList.toggle('is-updating', updating);
        item.querySelectorAll('button, input').forEach(el => {
          el.disabled = updating;
        });
      }
    }

    showError(message, retryFn) {
      // Remove any existing error
      this.clearError();

      const errorEl = document.createElement('div');
      errorEl.className = 'cart-drawer__error';
      errorEl.setAttribute('role', 'alert');
      errorEl.setAttribute('data-cart-error', '');

      const text = document.createElement('span');
      text.className = 'cart-drawer__error-text';
      text.textContent = message;
      errorEl.appendChild(text);

      const t = window.GoesslCartTranslations || {};
      if (retryFn) {
        const retryBtn = document.createElement('button');
        retryBtn.type = 'button';
        retryBtn.className = 'cart-drawer__error-retry';
        retryBtn.textContent = t.retry || 'Erneut versuchen';
        retryBtn.addEventListener('click', () => {
          this.clearError();
          retryFn();
        });
        errorEl.appendChild(retryBtn);
      }

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'cart-drawer__error-close';
      closeBtn.setAttribute('aria-label', t.close || 'Schließen');
      closeBtn.textContent = '×';
      closeBtn.addEventListener('click', () => this.clearError());
      errorEl.appendChild(closeBtn);

      // Insert at top of drawer body
      const body = this.drawer.querySelector(SELECTORS.body);
      if (body) {
        body.insertBefore(errorEl, body.firstChild);
      }

      // Auto-dismiss after 8 seconds
      this.errorTimeout = setTimeout(() => this.clearError(), 8000);
    }

    clearError() {
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
        this.errorTimeout = null;
      }
      const existing = this.drawer.querySelector('[data-cart-error]');
      if (existing) existing.remove();
    }

    async removeItem(key) {
      await this.setQuantity(key, 0);
    }

    async refresh() {
      try {
        // locale-aware root: on /en/ a bare '/?sections=' would render the drawer in German
        const response = await fetch(window.g26url('?sections=cart-drawer&_fd=0'), { signal: timeoutSignal(15000) });
        if (!response.ok) throw new Error('Fetch failed');
        const sections = await response.json();
        if (sections['cart-drawer']) this.renderSection(sections['cart-drawer']);
      } catch (error) {
        console.error('Cart refresh error:', error);
      }
    }

    /**
     * Swap the drawer's inner content for the server-rendered section markup.
     * The Liquid template is the single source of truth (sizes, delivery notes,
     * aria labels, money formatting) — no hand-built markup to drift.
     * Only innerHTML is replaced, so the drawer element, its open state and the
     * delegated listeners all survive.
     */
    renderSection(sectionHtml) {
      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const fresh = doc.querySelector(SELECTORS.drawer);
      if (!fresh) return;
      // r190: a duties line the customer opened stays open when + or − redraws the drawer (#901: the points note too)
      const opened = ['[data-duties]', '[data-points-note]'].filter((sel) => this.drawer.querySelector(sel)?.open);
      // a11y #902: the swap takes the focused control with it - focus its twin in the new markup
      const focusKey = this.pendingFocus || this.focusedKey();
      this.pendingFocus = null;
      this.drawer.innerHTML = fresh.innerHTML;
      for (const sel of opened) {
        const freshLine = this.drawer.querySelector(sel);
        if (freshLine) freshLine.open = true;
      }
      if (this.pendingNotice) { this.showNotice(this.pendingNotice); this.pendingNotice = null; }
      this.restoreFocus(focusKey);
    }

    // a11y #902: the focused control in the open drawer as a selector that finds its twin after a redraw
    // (its first data-* attribute: the line key on −/+/quantity/remove, the close and checkout markers)
    focusedKey() {
      const el = document.activeElement;
      if (!this.isOpen || !el || el === document.body || !this.drawer.contains(el)) return null;
      const attr = Array.from(el.attributes).find((a) => a.name.startsWith('data-'));
      return attr ? `[${attr.name}="${CSS.escape(attr.value)}"]` : null;
    }

    // Focus the twin (or the close button when the line is gone), unless something in the drawer has focus
    restoreFocus(focusKey) {
      if (!focusKey || !this.isOpen) return;
      const active = document.activeElement;
      if (active && active !== document.body && this.drawer.contains(active)) return;
      const tabbable = (sel) => Array.from(this.drawer.querySelectorAll(sel)).find((el) => el.tabIndex >= 0 && !el.disabled);
      (tabbable(focusKey) || tabbable(SELECTORS.closeBtn))?.focus({ preventScroll: true });
    }

    // r188: a calm note (not an error) at the top of the drawer body; stays until closed or redrawn
    showNotice(message) {
      const body = this.drawer.querySelector(SELECTORS.body);
      if (!body || !message) return;
      const el = document.createElement('div');
      el.className = 'cart-drawer__error cart-drawer__error--info';
      el.setAttribute('role', 'status');
      el.setAttribute('data-cart-notice', '');
      const text = document.createElement('span');
      text.className = 'cart-drawer__error-text';
      text.textContent = message;
      el.appendChild(text);
      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'cart-drawer__error-close';
      closeBtn.setAttribute('aria-label', (window.GoesslCartTranslations || {}).close || 'Schließen');
      closeBtn.textContent = '×';
      closeBtn.addEventListener('click', () => el.remove());
      el.appendChild(closeBtn);
      body.insertBefore(el, body.firstChild);
    }

    updateCartCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = count;
      });

      // Update live region. German uses the same word for singular and plural, but the
      // string still has to pluralise in other languages, so it goes through the shared
      // bridge, which picks cart.general.live_region.one / .other from the count.
      const liveRegion = document.getElementById('cart-live-region');
      if (liveRegion) {
        liveRegion.textContent = window.g26tText('cart.general.live_region', '{{ count }} Artikel im Warenkorb', { count: count });
      }
    }

    setLoading(loading) {
      this.drawer.classList.toggle('is-loading', loading);
    }




  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new CartDrawer());
  } else {
    new CartDrawer();
  }
})();
